import { useCallback, useState } from "react";
import usePagedList from "../../../hooks/usePagedList";
import * as crmApi from "../api/crmApi";
import { apiErrorCode } from "../api/crmErrors";
import { NUDGE_LIMIT, PAGE_SIZE } from "../contract";

/**
 * The Nudges zone (`GET /api/crm/nudges`): the first five, or — after "Show all" — every
 * open nudge a page at a time, in place.
 *
 * Dismiss and act are OPTIMISTIC: the card leaves at once (the spec's one-tap promise);
 * a refusal puts it back and rethrows so the caller can say why. The one exception is
 * `NUDGE_CLOSED`: someone (another tab, a helper) already handled it, so the card stays
 * gone, the list is re-read, and the caller still hears why.
 */
export default function useNudges() {
  const [showAll, setShowAll] = useState(false);
  const [hidden, setHidden] = useState(() => new Set());
  const params = showAll ? {} : { limit: NUDGE_LIMIT };
  const list = usePagedList(crmApi.listNudges, params, showAll ? PAGE_SIZE : NUDGE_LIMIT);
  const { reload } = list;

  const hide = (id, on) => setHidden((h) => {
    const next = new Set(h);
    if (on) next.add(id); else next.delete(id);
    return next;
  });

  const run = useCallback(async (id, call) => {
    hide(id, true);
    try {
      return await call();
    } catch (e) {
      if (apiErrorCode(e) === "NUDGE_CLOSED") reload();
      else hide(id, false);
      throw e;
    }
  }, [reload]);

  const dismiss = useCallback((id) => run(id, () => crmApi.dismissNudge(id)), [run]);
  const act = useCallback((id, payload) => run(id, () => crmApi.actOnNudge(id, payload)), [run]);

  const rows = list.rows.filter((n) => !hidden.has(n.id));
  const total = Math.max(0, list.total - (list.rows.length - rows.length));
  return { ...list, rows, total, showAll, setShowAll, dismiss, act };
}
